import { Injectable } from '@angular/core';
import {
  Router,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { JwtHelper } from '../utilities/jwt.helper';
import { NotificationService } from '../services/notification.service';
import { I18nService } from '../services/i18n.service';


/** Margem em segundos para considerar o token expirado antes do tempo real (evita race). */
const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService,
    private i18n: I18nService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    if (!this.authService.isAuthenticated()) {
      const sessionExpired = this.authService.getAndClearSessionExpired();
      if (sessionExpired) {
        this.notificationService.warning(this.i18n.translate('notifications.sessionExpired'));
      }
      this.router.navigate(['/login'], {
        queryParams: {
          returnUrl: state.url,
          ...(sessionExpired ? { sessionExpired: '1' } : {})
        },
        replaceUrl: true
      });
      return of(false);
    }

    const token = this.authService.getToken();
    if (token && JwtHelper.isTokenExpired(token, TOKEN_EXPIRY_BUFFER_SECONDS)) {
      this.authService.setSessionExpiredAndClear();
      this.notificationService.warning(this.i18n.translate('notifications.sessionExpired'));
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: state.url, sessionExpired: '1' },
        replaceUrl: true
      });
      return of(false);
    }

    return this.authService.validateSession().pipe(
      map(valid => {
        if (!valid) {
          this.notificationService.warning(this.i18n.translate('notifications.sessionExpired'));
          this.router.navigate(['/login'], {
            queryParams: { returnUrl: state.url, sessionExpired: '1' },
            replaceUrl: true
          });
          return false;
        }
        return true;
      })
    );
  }
}
